import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const AdministrationSystem = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [stats, setStats] = useState({});
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [userForm, setUserForm] = useState({
    username: '',
    full_name: '',
    email: '',
    phone: '',
    password: '',
    role_id: ''
  });

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([fetchUsers(), fetchRoles(), fetchStats()]);
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const data = await apiGet('/admin/administration/system/users');
      if (data.success) setUsers(data.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const data = await apiGet('/admin/administration/system/roles');
      if (data.success) setRoles(data.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await apiGet('/admin/administration/system/stats');
      if (data.success) setStats(data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmitUser = async () => {
    if (!userForm.username || !userForm.role_id || (!editingUser && !userForm.password)) {
      alert('Please fill all required fields');
      return;
    }

    try {
      const url = editingUser
        ? `${PRODUCTION_URL}/api/admin/administration/system/users/${editingUser.id}`
        : `${PRODUCTION_URL}/api/admin/administration/system/users`;

      const response = await fetch(url, {
        method: editingUser ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userForm)
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ User ${editingUser ? 'updated' : 'created'} successfully!`);
        setShowUserModal(false);
        setEditingUser(null);
        setUserForm({ username: '', full_name: '', email: '', phone: '', password: '', role_id: '' });
        fetchAllData();
      } else {
        alert(data.message || 'Error saving user');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Error saving user');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      username: user.username,
      full_name: user.full_name || '',
      email: user.email || '',
      phone: user.phone || '',
      password: '',
      role_id: user.role_id || ''
    });
    setShowUserModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/administration/system/users/${userId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage('✅ User deleted successfully!');
        fetchAllData();
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : isTablet ? '20px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: isMobile ? '12px' : '20px',
      marginBottom: isMobile ? '20px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '20px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '20px' : isTablet ? '26px' : '32px',
      borderRadius: isMobile ? '16px' : isTablet ? '18px' : '20px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px 16px 4px' : '0 0 24px 0'
    },
    sectionTitle: {
      fontSize: isMobile ? '18px' : isTablet ? '20px' : '24px',
      fontWeight: '800',
      color: colors.textDark,
      marginBottom: isMobile ? '16px' : isTablet ? '20px' : '24px',
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '8px' : '12px',
      justifyContent: 'space-between'
    },
    button: {
      padding: isMobile ? '8px 14px' : '10px 18px',
      borderRadius: '8px',
      border: 'none',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: colors.primary,
      color: colors.white
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '13px'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '20px' : '40px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '20px' : '32px',
      maxWidth: isMobile ? '100%' : '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      marginBottom: '12px',
      fontFamily: 'inherit'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading system data...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {successMessage && (
        <div style={{
          padding: isMobile ? '12px 16px' : '14px 20px',
          borderRadius: '10px',
          marginBottom: '16px',
          fontSize: isMobile ? '13px' : '14px',
          fontWeight: '600',
          background: 'rgba(74, 124, 42, 0.15)',
          color: colors.success,
          border: `2px solid ${colors.success}`,
          margin: isMobile ? '0 4px 12px 4px' : '0 0 16px 0'
        }}>
          {successMessage}
        </div>
      )}

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>System Administration</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Users & roles' : 'Manage system users, roles, permissions, and administrative settings'}
        </p>
      </div>

      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.primary}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', marginBottom: '8px', color: colors.primary }}>
            {stats.totalUsers || 0}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Total Users
          </div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.success}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', marginBottom: '8px', color: colors.success }}>
            {stats.activeUsers || 0}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Active Users
          </div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.accent}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', marginBottom: '8px', color: colors.accent }}>
            {stats.totalRoles || 0}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Total Roles
          </div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.warning}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', marginBottom: '8px', color: colors.warning }}>
            {stats.recentLogins || 0}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Logins (24h)
          </div>
        </div>
      </div>

      <div style={styles.contentCard}>
        <div style={styles.sectionTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>👨‍💼</span>
            <span>Users</span>
          </div>
          <button onClick={() => { setEditingUser(null); setUserForm({ username: '', full_name: '', email: '', phone: '', password: '', role_id: '' }); setShowUserModal(true); }} style={styles.button}>
            + Add User
          </button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ background: colors.lightGray, fontWeight: '700', textAlign: 'left', padding: isMobile ? '10px' : '12px', borderBottom: `2px solid ${colors.mediumGray}` }}>Username</th>
                {!isMobile && <th style={{ background: colors.lightGray, fontWeight: '700', textAlign: 'left', padding: '12px', borderBottom: `2px solid ${colors.mediumGray}` }}>Full Name</th>}
                <th style={{ background: colors.lightGray, fontWeight: '700', textAlign: 'left', padding: isMobile ? '10px' : '12px', borderBottom: `2px solid ${colors.mediumGray}` }}>Role</th>
                <th style={{ background: colors.lightGray, fontWeight: '700', textAlign: 'left', padding: isMobile ? '10px' : '12px', borderBottom: `2px solid ${colors.mediumGray}` }}>Status</th>
                <th style={{ background: colors.lightGray, fontWeight: '700', textAlign: 'left', padding: isMobile ? '10px' : '12px', borderBottom: `2px solid ${colors.mediumGray}` }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td style={{ padding: isMobile ? '10px' : '12px', borderBottom: `1px solid ${colors.lightGray}` }}><strong>{user.username}</strong></td>
                  {!isMobile && <td style={{ padding: '12px', borderBottom: `1px solid ${colors.lightGray}` }}>{user.full_name || '-'}</td>}
                  <td style={{ padding: isMobile ? '10px' : '12px', borderBottom: `1px solid ${colors.lightGray}` }}>{user.role_name || 'No Role'}</td>
                  <td style={{ padding: isMobile ? '10px' : '12px', borderBottom: `1px solid ${colors.lightGray}` }}>
                    <span style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', background: user.is_active ? `${colors.success}20` : `${colors.danger}20`, color: user.is_active ? colors.success : colors.danger }}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: isMobile ? '10px' : '12px', borderBottom: `1px solid ${colors.lightGray}` }}>
                    <button onClick={() => handleEditUser(user)} style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '11px', fontWeight: '600', cursor: 'pointer', background: colors.accent, color: colors.white, marginRight: '6px' }}>
                      ✏️ Edit
                    </button>
                    {user.username !== 'admin' && (
                      <button onClick={() => handleDeleteUser(user.id)} style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '11px', fontWeight: '600', cursor: 'pointer', background: colors.danger, color: colors.white }}>
                        🗑️
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={styles.contentCard}>
        <div style={styles.sectionTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🎭</span>
            <span>Roles</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '12px' }}>
          {roles.map(role => (
            <div key={role.id} style={{ padding: isMobile ? '14px' : '16px', background: colors.lightGray, borderRadius: '10px', border: `1px solid ${colors.mediumGray}` }}>
              <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700', marginBottom: '6px', color: colors.primary }}>
                {role.role_name}
              </div>
              <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '8px' }}>
                {role.role_description || 'No description'}
              </div>
              <div style={{ fontSize: '11px', color: colors.textLight }}>
                {role.user_count} user{role.user_count !== 1 ? 's' : ''}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showUserModal && (
        <div style={styles.modal} onClick={() => setShowUserModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '20px', fontWeight: '700' }}>
              {editingUser ? 'Edit User' : 'Add New User'}
            </h3>
            <input type="text" placeholder="Username *" value={userForm.username} onChange={(e) => setUserForm({ ...userForm, username: e.target.value })} style={styles.input} disabled={!!editingUser} />
            <input type="text" placeholder="Full Name" value={userForm.full_name} onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })} style={styles.input} />
            <input type="email" placeholder="Email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} style={styles.input} />
            <input type="tel" placeholder="Phone" value={userForm.phone} onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })} style={styles.input} />
            {!editingUser && <input type="password" placeholder="Password *" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} style={styles.input} />}
            <select value={userForm.role_id} onChange={(e) => setUserForm({ ...userForm, role_id: e.target.value })} style={{ ...styles.input, background: colors.white }}>
              <option value="">Select Role *</option>
              {roles.map(role => <option key={role.id} value={role.id}>{role.role_name}</option>)}
            </select>
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button onClick={handleSubmitUser} style={{ ...styles.button, flex: 1 }}>{editingUser ? 'Update User' : 'Create User'}</button>
              <button onClick={() => setShowUserModal(false)} style={{ ...styles.button, background: colors.mediumGray, color: colors.textDark, flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdministrationSystem;